"use client"

import type React from "react"
import { useState, useEffect } from "react"
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonLoading,
  IonButtons,
  IonBackButton,
  IonToast,
  IonSegment,
  IonSegmentButton,
  IonLabel,
} from "@ionic/react"
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Badge,
  Tooltip,
  TablePagination,
  InputAdornment,
  LinearProgress,
} from "@mui/material"
import { styled } from "@mui/material/styles"
import {
  Check,
  ArrowBack,
  Search,
  FilterList,
  Visibility,
  Refresh,
  CheckCircle,
  Pending,
  Cancel,
  Edit,
  Save,
  Close,
  ShoppingCart,
  LocalShipping,
} from "@mui/icons-material"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { 
  getMyPurchaseOrderRequests,
  getMyPurchaseOrderRequestsByStage,
  getWarehouseStocks,
  updateRequestStatus
} from "../../api/accounting/accounting"
import { getAllApprovedVendors } from "../../api/vendor.service"

const StyledCard = styled(Card)(({ theme }) => ({
  margin: theme.spacing(2),
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[3],
  [theme.breakpoints.down("sm")]: {
    margin: theme.spacing(1),
    padding: theme.spacing(2),
  },
}))

const PageContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1),
  },
}))

// Define types based on your API response
interface RequestItem {
  id: number
  item_id: string
  stock_name: string
  requested_qty: string
  vendor_id: string
  status: string
  audited_qty?: string
  cmd_approved_qty?: string
  supplied_qty?: string
  po_approved_qty?: string | null
  po_received_qty?: string | null
  purchase_requisition_id?: string
  notes?: string
  unit_price?: string
  available_quantity?: string
  po_supplied_status?: string
  // For editing
  editMode?: boolean
}

interface PurchaseOrder {
  id: number
  po_number: string
  notes: string | null
  status: string
  created_by: string
  requested_by_name: string
  created_at: string
  approved_at: string | null
  approved_by: string | null
  cmd_approved_at: string | null
  cmd_approved_by: string | null
  cmd_approval_status: string
  audit_approval_status: string
  branch_id: string
  business_id: string
  items: RequestItem[]
  total_value?: string
}

interface Vendor {
  id: string
  name: string
  contact: string
}

const PurchaseOfficerPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'raised' | 'po_approved' | 'cmd_approved' | 'supplied' | 'audited'>('raised')
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([])
  const [filteredOrders, setFilteredOrders] = useState<PurchaseOrder[]>([])
  const [selectedOrder, setSelectedOrder] = useState<PurchaseOrder | null>(null)
  const [showDetailDialog, setShowDetailDialog] = useState(false)
  const [loading, setLoading] = useState(false)
  const [updating, setUpdating] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [showSuppliedConfirmDialog, setShowSuppliedConfirmDialog] = useState(false)
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    vendor_id: 'all',
    start_date: null as Date | null,
    end_date: null as Date | null,
  })
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalCount, setTotalCount] = useState(0)

  const [vendors, setVendors] = useState<Vendor[]>([])

  const statusOptions = [
    { value: 'draft', label: 'Draft', color: 'default' },
    { value: 'pending', label: 'Pending Approval', color: 'warning' },
    { value: 'po_approved', label: 'PO Approved', color: 'info' },
    { value: 'cmd_approved', label: 'CMD Approved', color: 'success' },
    { value: 'in_progress', label: 'In Progress', color: 'info' },
    { value: 'completed', label: 'Completed', color: 'success' },
  ]

  useEffect(() => {
    fetchVendors()
    fetchPurchaseOrders()
  }, [activeTab, page, rowsPerPage])

  const fetchVendors = async () => {
    try {
      const response = await getAllApprovedVendors()
      console.log('Vendors Response:', response)

      if (response && response.data) {
        // Map API response to Component used Vendor interface
        // The API returns vendor_id, vendor_name, phone
        // The component expects id, name, contact
        const mappedVendors: Vendor[] = response.data.map((v: any) => ({
          id: v.vendor_id,
          name: v.vendor_name,
          contact: v.phone || ''
        }))
        setVendors(mappedVendors)
      }
    } catch (error) {
      console.error("Failed to fetch vendors", error)
      showErrorToast('Failed to fetch vendors')
    }
  }

  useEffect(() => {
    applyFilters()
  }, [purchaseOrders, filters])

  const fetchPurchaseOrders = async () => {
    setLoading(true)
    try {
      const params = {
        page: (page + 1).toString(),
        per_page: rowsPerPage.toString(),
        ...(filters.search && { search: filters.search }),
        ...(filters.vendor_id !== 'all' && { vendor_id: filters.vendor_id }),
      }

      // Add status filter based on active tab
      // For 'raised': items with status='pending' (not yet PO approved)
      // For 'po_approved': items with status='po_approved' 
      // For other tabs, use cmd_approval_status
      let statusFilter = ''
      let cmdApprovalFilter = ''
      
      switch (activeTab) {
        case 'raised':
          statusFilter = 'pending' // Items not yet PO approved
          break
        case 'po_approved':
          statusFilter = 'po_approved' // Items PO approved
          cmdApprovalFilter = 'pending' // CMD not yet approved
          break
        case 'cmd_approved':
          cmdApprovalFilter = 'approved' // CMD approved
          break
        case 'supplied':
          statusFilter = 'supplied' // Supplied orders
          break
        case 'audited':
          statusFilter = 'audited' // Audited orders
          break
      }

      const finalParams = {
        ...params,
        ...(statusFilter && { status: statusFilter }),
        ...(cmdApprovalFilter && { cmd_approval_status: cmdApprovalFilter }),
        // Also pass the tab/stage info for additional backend filtering if needed
        ...(activeTab && { stage: activeTab })
      }

      const response = await getMyPurchaseOrderRequestsByStage(finalParams)
      console.log('Purchase Orders  stage Response:', response)

      if (response.data && response.data.data) {
        const orders = response.data.data.map((order: any) => ({
          id: order.id,
          po_number: order.po_number,
          notes: order.notes,
          status: order.status,
          created_by: order.created_by,
          requested_by_name: order.requested_by_name,
          created_at: order.created_at,
          approved_at: order.approved_at,
          approved_by: order.approved_by,
          cmd_approved_at: order.cmd_approved_at,
          cmd_approved_by: order.cmd_approved_by,
          cmd_approval_status: order.cmd_approval_status || 'pending',
          audit_approval_status: order.audit_approval_status || 'pending',
          branch_id: order.branch_id,
          business_id: order.business_id,
          total_value: order.total_value,
          items: order.items.map((item: any) => ({
            id: item.id,
            item_id: item.item_id,
            stock_name: item.stock_name,
            requested_qty: item.requested_qty,
            vendor_id: item.vendor_id,
            status: item.status,
            audited_qty: item.audited_qty || '0',
            cmd_approved_qty: item.cmd_approved_qty || '0',
            supplied_qty: item.supplied_qty || '0',
            po_approved_qty: item.po_approved_qty || item.requested_qty,
            po_received_qty: item.po_received_qty || '',
            po_supplied_status: item.po_supplied_status || 'pending',
            purchase_requisition_id: item.purchase_requisition_id,
            notes: item.notes,
            unit_price: item.unit_price,
            available_quantity: item.available_quantity,
            editMode: false
          }))
        }))

        setPurchaseOrders(orders)
        setTotalCount(orders.length)
      }
    } catch (error) {
      console.error("Failed to fetch purchase orders")
      showErrorToast('Failed to fetch purchase orders')
    } finally {
      setLoading(false)
    }
  }

  const applyFilters = () => {
    let filtered = [...purchaseOrders]

    if (filters.search) {
      const query = filters.search.toLowerCase()
      filtered = filtered.filter(order =>
        order.po_number.toLowerCase().includes(query) ||
        order.requested_by_name.toLowerCase().includes(query) ||
        order.items.some(item => item.stock_name.toLowerCase().includes(query))
      )
    }

    if (filters.vendor_id !== 'all') {
      filtered = filtered.filter(order => 
        order.items.some(item => item.vendor_id === filters.vendor_id)
      )
    }

    setFilteredOrders(filtered)
  }

  const showSuccessToast = (message: string) => {
    setToastMessage(message)
    setToastType('success')
    setShowToast(true)
  }

  const showErrorToast = (message: string) => {
    setToastMessage(message)
    setToastType('error')
    setShowToast(true)
  }

  const handleViewOrder = (order: PurchaseOrder) => {
    setSelectedOrder(order)
    setShowDetailDialog(true)
  }

  const handleUpdateItemQuantity = (orderId: number, itemId: number, field: string, value: string) => {
    setPurchaseOrders(prev => prev.map(order => {
      if (order.id === orderId) {
        return {
          ...order,
          items: order.items.map(item => {
            if (item.id === itemId) {
              return { ...item, [field]: value }
            }
            return item
          })
        }
      }
      return order
    }))

    if (selectedOrder && selectedOrder.id === orderId) {
      setSelectedOrder(prev => {
        if (!prev) return null
        return {
          ...prev,
          items: prev.items.map(item => {
            if (item.id === itemId) {
              return { ...item, [field]: value }
            }
            return item
          })
        }
      })
    }
  }

  const saveOrderChanges = async (orderId?: number) => {
    if (!orderId) return
    const order = purchaseOrders.find(o => o.id === orderId)
    if (!order) return

    setUpdating(true)
    try {
      const payload = {
        purchase_requisition_id: order.po_number,
        action: 'po_approve_items',
        items: order.items.map(item => ({
             item_id: item.item_id,
             po_approved_qty: item.po_approved_qty || item.requested_qty,
             po_received_qty: item.po_received_qty,
             notes: item.notes
        }))
      }

      console.log('Saving ordersssssss changes:', payload)
      const response = await updateRequestStatus(payload)
      console.log('Save response:', response)

      if (response && (response.status === 200 || response.status === true || response.data?.status)) {
         showSuccessToast('Order updated successfully')
         setShowDetailDialog(false)
         fetchPurchaseOrders() 
      } else {
         showSuccessToast('Order updated locally (API pending)')
         setShowDetailDialog(false)
      }
    } catch (error) {
      console.error('Error saving order:', error)
      showErrorToast('Failed to save changes')
    } finally {
      setUpdating(false)
    }
  }

  // Function to approve the PO (save + mark as approved)
  const approveOrder = async (orderId?: number) => {
    if (!orderId) return
    const order = purchaseOrders.find(o => o.id === orderId)
    if (!order) return

    setUpdating(true)
    try {
      const payload = {
        purchase_requisition_id: order.po_number,
        action: 'po_approve_items', // Action for PO approval
        items: order.items.map(item => ({
             item_id: item.item_id,
             po_approved_qty: item.po_approved_qty || item.requested_qty,
             notes: item.notes
        }))
      }

      console.log('Approving PO:', payload)
      const response = await updateRequestStatus(payload)
      console.log('Approve response:', response)

      if (response && (response.status === 200 || response.status === true || response.data?.status)) {
         showSuccessToast('Purchase Order approved successfully')
         setShowDetailDialog(false)
         fetchPurchaseOrders() 
      } else {
         showSuccessToast('PO approved locally (API pending)')
         setShowDetailDialog(false)
      }
    } catch (error) {
      console.error('Error approving PO:', error)
      showErrorToast('Failed to approve Purchase Order')
    } finally {
      setUpdating(false)
    }
  }

  // Function to save supplied items (for cmd_approved tab)
  const saveSuppliedItems = async (orderId?: number) => {
    if (!orderId) return
    const order = purchaseOrders.find(o => o.id === orderId)
    if (!order) return

    setUpdating(true)
    try {
      const payload = {
        purchase_requisition_id: order.po_number,
        action: 'po_supplied_items', // Action for saving supplied quantities
        items: order.items.map(item => ({
             item_id: item.item_id,
             po_received_qty: item.po_received_qty || '0',
             notes: item.notes
        }))
      }

      console.log('Saving supplied items:', payload)
      const response = await updateRequestStatus(payload)
      console.log('Supplied items response:', response)

      if (response && (response.status === 200 || response.status === true || response.data?.status)) {
         showSuccessToast('Supplied quantities saved successfully')
         setShowDetailDialog(false)
         fetchPurchaseOrders() 
      } else {
         showSuccessToast('Supplied quantities saved locally (API pending)')
         setShowDetailDialog(false)
      }
    } catch (error) {
      console.error('Error saving supplied items:', error)
      showErrorToast('Failed to save supplied quantities')
    } finally {
      setUpdating(false)
    }
  }

  const getStatusChip = (status: string) => {
    const statusMap: any = {
      draft: { label: 'Draft', color: 'default', icon: <Pending /> },
      pending: { label: 'Pending', color: 'warning', icon: <Pending /> },
      po_approved: { label: 'PO Approved', color: 'info', icon: <CheckCircle /> },
      cmd_approved: { label: 'CMD Approved', color: 'success', icon: <CheckCircle /> },
      in_progress: { label: 'In Progress', color: 'info', icon: <LocalShipping /> },
      completed: { label: 'Completed', color: 'success', icon: <CheckCircle /> },
    }

    const statusInfo = statusMap[status] || { label: status, color: 'default', icon: null }

    return (
      <Chip
        label={statusInfo.label}
        color={statusInfo.color as any}
        size="small"
        icon={statusInfo.icon}
        variant="outlined"
      />
    )
  }

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }))
    setPage(0)
  }

  const renderPendingTab = () => (
    <>
    

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: "#fff3e0", borderLeft: "4px solid #ff9800" }}>
            <CardContent>
              <Typography variant="h3" sx={{ fontWeight: 700, color: "#ff9800" }}>
                {purchaseOrders.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {activeTab === 'raised' && 'Raised Orders'}
                {activeTab === 'po_approved' && 'PO Approved Orders'}
                {activeTab === 'cmd_approved' && 'CMD Approved Orders'}
                {activeTab === 'supplied' && 'Supplied Orders'}
                {activeTab === 'audited' && 'Audited Orders'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: "#e8f5e9", borderLeft: "4px solid #4caf50" }}>
            <CardContent>
              <Typography variant="h3" sx={{ fontWeight: 700, color: "#4caf50" }}>
                {purchaseOrders.reduce((sum, order) => sum + order.items.length, 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Items
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: "#e3f2fd", borderLeft: "4px solid #2196f3" }}>
            <CardContent>
              <Typography variant="h3" sx={{ fontWeight: 700, color: "#2196f3" }}>
                {vendors.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Vendors
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: "#f3e5f5", borderLeft: "4px solid #9c27b0" }}>
            <CardContent>
              <Typography variant="h3" sx={{ fontWeight: 700, color: "#9c27b0" }}>
                {purchaseOrders.reduce((sum, order) => {
                  const totalRequested = order.items.reduce((s, item) => s + parseFloat(item.requested_qty), 0)
                  return sum + totalRequested
                }, 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Units
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterList /> Filters
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Search PO Number or Item"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Vendor</InputLabel>
                <Select
                  value={filters.vendor_id}
                  label="Vendor"
                  onChange={(e) => handleFilterChange('vendor_id', e.target.value)}
                >
                  <MenuItem value="all">All Vendors</MenuItem>
                  {vendors.map((vendor) => (
                    <MenuItem key={vendor.id} value={vendor.id}>
                      {vendor.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <DatePicker
                label="Start Date"
                value={filters.start_date}
                onChange={(newValue) => handleFilterChange('start_date', newValue)}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <DatePicker
                label="End Date"
                value={filters.end_date}
                onChange={(newValue) => handleFilterChange('end_date', newValue)}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} md={1}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setFilters({
                  search: '',
                  vendor_id: 'all',
                  start_date: null,
                  end_date: null,
                })}
                size="small"
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Purchase Orders Table */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">
              Pending Purchase Orders ({filteredOrders.length})
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={fetchPurchaseOrders}
                disabled={loading}
              >
                Refresh
              </Button>
            </Box>
          </Box>

          {loading ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <CircularProgress />
            </Box>
          ) : filteredOrders.length === 0 ? (
            <Alert severity="info" sx={{ textAlign: 'center', py: 3 }}>
              No pending purchase orders
            </Alert>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>PO Number</strong></TableCell>
                      <TableCell><strong>Requested By</strong></TableCell>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>Items</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredOrders.map((order) => (
                      <TableRow key={order.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {order.po_number}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>{order.requested_by_name}</Typography>
                        </TableCell>
                        <TableCell>
                          {new Date(order.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Typography>{order.items.length} items</Typography>
                        </TableCell>
                        <TableCell>
                          {getStatusChip(order.status)}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                color="info"
                                onClick={() => handleViewOrder(order)}
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10))
                  setPage(0)
                }}
              />
            </>
          )}
        </CardContent>
      </Card>
    </>
  )

  const renderApprovedTab = () => (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Approved/Completed Orders
        </Typography>
        <Alert severity="info">
          This tab shows all orders that have been processed. Implementation pending.
        </Alert>
      </CardContent>
    </Card>
  )

  return (
    <IonPage className="pageDown">

    


      <IonHeader>
        <IonToolbar>
            <Box
        sx={{
          bgcolor: "#2e7d32",
          color: "white",
          py: 3,
          px: 4,
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
          <IconButton sx={{ color: "white" }} onClick={() => window.history.back()}>
            <ArrowBack />
          </IconButton>
          <ShoppingCart sx={{ fontSize: 32 }} />
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Purchase Officer Dashboard
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ opacity: 0.9, ml: 7 }}>
          Manage purchase orders and track supplier deliveries.  jjjj
        </Typography>
      </Box>

      </IonToolbar>
        <IonToolbar>
          <IonSegment 
            value={activeTab} 
            onIonChange={(e) => setActiveTab(e.detail.value as any)}
            scrollable
          >
            <IonSegmentButton value="raised">
              <IonLabel>Raised</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="po_approved">
              <IonLabel>PO Approved</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="cmd_approved">
              <IonLabel>CMD Approved</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="supplied">
              <IonLabel>Supplied</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="audited">
              <IonLabel>Audited</IonLabel>
            </IonSegmentButton>
          </IonSegment>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <PageContainer>
          {renderPendingTab()}
        </PageContainer>

        {/* Detail Dialog */}
        <Dialog 
          open={showDetailDialog} 
          onClose={() => setShowDetailDialog(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                Purchase Order: {selectedOrder?.po_number}
              </Typography>
              <IconButton onClick={() => setShowDetailDialog(false)}>
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedOrder && (
              <>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">Requested By</Typography>
                    <Typography variant="body1">{selectedOrder.requested_by_name}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">Created Date</Typography>
                    <Typography variant="body1">{new Date(selectedOrder.created_at).toLocaleString()}</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">Order Status</Typography>
                    {getStatusChip(selectedOrder.status)}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">CMD Approval</Typography>
                    {getStatusChip(selectedOrder.cmd_approval_status)}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">Audit Status</Typography>
                    {getStatusChip(selectedOrder.audit_approval_status)}
                  </Grid>
                  {selectedOrder.notes && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">Notes</Typography>
                      <Typography variant="body1">{selectedOrder.notes}</Typography>
                    </Grid>
                  )}
                </Grid>

                <Typography variant="h6" sx={{ mb: 2 }}>Items</Typography>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Item</TableCell>
                        <TableCell>Vendor</TableCell>
                        <TableCell align="right">Requested</TableCell>
                        <TableCell align="right">PO Approved Qty</TableCell>
                        {/* <TableCell align="right">Vendor Supplied</TableCell> */}
                        <TableCell align="right">PO Received Qty</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedOrder.items.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <Typography variant="body2">{item.stock_name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              ID: {item.item_id}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={item.vendor_id} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell align="right">{item.requested_qty}</TableCell>
                          <TableCell align="right">
                            
                            {activeTab === 'raised' && item.status === 'pending' ? (
                              <TextField
                                size="small"
                                type="number"
                                value={item.po_approved_qty || item.requested_qty}
                                onChange={(e) => handleUpdateItemQuantity(selectedOrder.id, item.id, 'po_approved_qty', e.target.value)}
                                sx={{ width: 100 }}
                                InputProps={{ inputProps: { min: 0 } }}
                              />
                            ) : (
                              item.po_approved_qty || item.cmd_approved_qty || '-'
                            )}
                          </TableCell>
                          {/* <TableCell align="right">
                            <Chip 
                              label={item.supplied_qty || '0'} 
                              size="small" 
                              color={item.supplied_qty && parseFloat(item.supplied_qty) > 0 ? 'success' : 'default'}
                            />
                          </TableCell> */}
                          <TableCell align="right">
                            {/* Only allow editing PO Received Qty on 'cmd_approved' tab AND when not already approved */}
                            {activeTab === 'cmd_approved' && item.po_supplied_status !== 'approved' ? (
                              <TextField
                                size="small"
                                type="number"
                                value={item.po_received_qty || ''}
                                onChange={(e) => handleUpdateItemQuantity(selectedOrder.id, item.id, 'po_received_qty', e.target.value)}
                                placeholder="Received"
                                sx={{ width: 100 }}
                                InputProps={{ inputProps: { min: 0 } }}
                              />
                            ) : (
                              item.po_received_qty || '-'
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDetailDialog(false)}>Close</Button>
            
            {/* For 'raised' tab: Combined Save Changes & Approve button - only show if any item is pending */}
            {activeTab === 'raised' && selectedOrder?.items?.some(item => item.status === 'pending') && (
              <Button 
                variant="contained" 
                color="success" 
                onClick={() => setShowConfirmDialog(true)}
                disabled={updating}
                startIcon={<Check />}
              >
                {updating ? <CircularProgress size={24} color="inherit" /> : 'Save Changes & Approve'}
              </Button>
            )}
            
            {/* For 'cmd_approved' tab: Save Received Qty button with confirmation - only show if any item is not yet supplied */}
            {activeTab === 'cmd_approved' && selectedOrder?.items?.some(item => item.po_supplied_status !== 'approved') && (
              <Button 
                variant="contained" 
                color="primary" 
                onClick={() => setShowSuppliedConfirmDialog(true)}
                disabled={updating}
                startIcon={<Save />}
              >
                {updating ? <CircularProgress size={24} color="inherit" /> : 'Save Received Qty'}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        

        {/* Confirmation Dialog */}
        <Dialog
          open={showConfirmDialog}
          onClose={() => setShowConfirmDialog(false)}
        >
          <DialogTitle>Confirm PO Approval</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to save changes and approve this Purchase Order?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              PO Number: <strong>{selectedOrder?.po_number}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Once approved, the quantities cannot be edited.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowConfirmDialog(false)} color="inherit">
              Cancel
            </Button>
            <Button 
              variant="contained" 
              color="success" 
              onClick={() => {
                setShowConfirmDialog(false)
                approveOrder(selectedOrder?.id)
              }}
              startIcon={<Check />}
            >
              Confirm & Approve
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirmation Dialog for Supplied Items (CMD Approved tab) */}
        <Dialog
          open={showSuppliedConfirmDialog}
          onClose={() => setShowSuppliedConfirmDialog(false)}
        >
          <DialogTitle>Confirm Received Quantities</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to save the received quantities for this Purchase Order?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              PO Number: <strong>{selectedOrder?.po_number}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This will record the quantities received from the vendor.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowSuppliedConfirmDialog(false)} color="inherit">
              Cancel
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => {
                setShowSuppliedConfirmDialog(false)
                saveSuppliedItems(selectedOrder?.id)
              }}
              startIcon={<Save />}
            >
              Confirm & Save
            </Button>
          </DialogActions>
        </Dialog>

        <IonLoading isOpen={loading || updating} message={loading ? 'Loading...' : 'Saving...'} />
        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={3000}
          color={toastType}
        />
      </IonContent>
    </IonPage>
  )
}

export default PurchaseOfficerPage
